import type {
  PipelineStage,
  PipelineStageKey,
  RollForwardResult,
  UpdateStagePayload,
} from "@/features/pipeline/types";
import { apiFetch } from "@/lib/api";

/** The Django API wraps every body in `{ success, message, data }`. */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiPipelineStage {
  id: string;
  stage: PipelineStageKey;
  stage_label: string;
  position: number;
  status: PipelineStage["status"];
  assignee: number | null;
  assignee_name: string | null;
  notes: string;
  started_at: string | null;
  completed_at: string | null;
}

interface ApiRollForward {
  id: string;
  tax_years: string;
}

function toStage(a: ApiPipelineStage): PipelineStage {
  return {
    id: a.id,
    stage: a.stage,
    stageLabel: a.stage_label,
    position: a.position,
    status: a.status,
    assignee: a.assignee,
    assigneeName: a.assignee_name,
    notes: a.notes ?? "",
    startedAt: a.started_at,
    completedAt: a.completed_at,
  };
}

/** GET /cases/:caseId/pipeline/ — the 15 ordered stages (plain array in `data`). */
export async function getPipeline(caseId: string): Promise<PipelineStage[]> {
  const body = await apiFetch<Envelope<ApiPipelineStage[]>>(
    `/cases/${caseId}/pipeline/`,
  );
  return body.data.map(toStage);
}

/** PATCH /cases/:caseId/pipeline/:stage/ — `stage` is the stage KEY (e.g. BANK_DATA). */
export async function updateStage(
  caseId: string,
  stage: PipelineStageKey,
  payload: UpdateStagePayload,
): Promise<PipelineStage> {
  const body = await apiFetch<Envelope<ApiPipelineStage>>(
    `/cases/${caseId}/pipeline/${stage}/`,
    { method: "PATCH", json: payload },
  );
  return toStage(body.data);
}

/** POST /cases/:caseId/pipeline/roll-forward/ — opens the next-year case.
 * Only team-lead/CD/GA/SA may call it; others get a 403 (surfaced as a toast). */
export async function rollForward(caseId: string): Promise<RollForwardResult> {
  const body = await apiFetch<Envelope<ApiRollForward>>(
    `/cases/${caseId}/pipeline/roll-forward/`,
    { method: "POST" },
  );
  return { id: body.data.id, taxYears: body.data.tax_years };
}
